import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Button, Grid, Chip, IconButton, 
    Table, TableBody, TableCell, TableContainer, TableHead, 
    TableRow, CircularProgress, Alert, List, ListItem, ListItemText,
    TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import StayFeeFormModal from '../../components/StayFeeFormModal';
import { RateService } from '../../api/services/rateService';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function OperationalFeesTab({ setNotification }) {
    const { currency } = useCurrency();
    const [stayFees, setStayFees] = useState([]);
    const [feesLoading, setFeesLoading] = useState(false);
    const [isSavingFee, setIsSavingFee] = useState(false);
    
    const [feesPage, setFeesPage] = useState(0);
    const [feesRowsPerPage, setFeesRowsPerPage] = useState(20);
    const [totalFees, setTotalFees] = useState(0);

    const [feeModal, setFeeModal] = useState({ open: false, data: null });

    useEffect(() => {
        fetchStayFees();
    }, [feesPage, feesRowsPerPage]);

    const fetchStayFees = async () => {
        setFeesLoading(true);
        try {
            const response = await RateService.getStayFees({ page: feesPage, limit: feesRowsPerPage });
            const feesArray = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
            setStayFees(feesArray);
            setTotalFees(extractTotalCount(response, feesArray));
        } catch (err) {
            console.error('Failed to fetch stay fees:', err);
            setNotification({ open: true, message: 'Failed to load stay fees.', severity: 'error' });
        } finally {
            setFeesLoading(false);
        }
    };

    const handleSaveStayFee = async (feeData) => {
        setIsSavingFee(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            
            if (feeModal.data) {
                await RateService.updateStayFee(feeModal.data.id, feeData, tenantId);
                setNotification({ open: true, message: 'Stay fee updated successfully!', severity: 'success' });
            } else {
                await RateService.createStayFee(feeData, tenantId);
                setNotification({ open: true, message: 'Stay fee created successfully!', severity: 'success' });
            }
            
            setFeeModal({ open: false, data: null });
            await fetchStayFees();
        } catch (err) {
            console.error('Failed to save stay fee:', err);
            if (err.response?.status === 403 || err.response?.status === 405) {
                setNotification({ open: true, message: 'Action not allowed while the hotel is suspended.', severity: 'error' });
            } else {
                setNotification({ open: true, message: 'Failed to save stay fee. Please try again.', severity: 'error' });
            }
        } finally {
            setIsSavingFee(false);
        }
    };

    // Server-side pagination is now used.

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Configuration</Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Manage the standard operational fees that are automatically applied to guest folios based on their stay criteria.
                        </Typography>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} md={9}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Active Fee Rules</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton onClick={fetchStayFees} disabled={feesLoading} size="small">
                                <RefreshIcon />
                            </IconButton>
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon />} 
                                size="small"
                                onClick={() => setFeeModal({ open: true, data: null })}
                            >
                                Add Fee
                            </Button>
                        </Box>
                    </Box>
                    <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fee Type</TableCell>
                                        <TableCell>Calculation</TableCell>
                                        <TableCell>Value</TableCell>
                                        <TableCell>Threshold Time</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {feesLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell>
                                        </TableRow>
                                    ) : stayFees.length > 0 ? (
                                        stayFees.map(fee => (
                                            <TableRow key={fee.id} hover>
                                                <TableCell><b>{fee.type}</b></TableCell>
                                                <TableCell>{fee.feeType}</TableCell>
                                                <TableCell>{fee.feeType === 'Percentage' ? `${fee.value}%` : formatCurrency(fee.value, currency)}</TableCell>
                                                <TableCell>{fee.thresholdTime || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip label={fee.status} size="small" color={fee.status === 'Active' ? 'success' : 'default'} variant="soft" />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" color="primary" onClick={() => setFeeModal({ open: true, data: fee })}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                <Typography color="text.secondary">No operational fees configured.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={stayFees.length === 0 ? 0 : totalFees}
                                page={feesPage}
                                onPageChange={(e, p) => setFeesPage(p)}
                                rowsPerPage={feesRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setFeesRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[20, 50, 100]}
                            />
                    </TableContainer>
                    <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                        Stay fees are automatic charges (like Early Check-In or Late Check-Out) added to the guest's folio.
                    </Alert>
                </Grid>
            </Grid>

            <StayFeeFormModal 
                open={feeModal.open}
                onClose={() => setFeeModal({ open: false, data: null })}
                onSave={handleSaveStayFee}
                editingFee={feeModal.data}
                loading={isSavingFee}
            />
        </Box>
    );
}
